import {
  useTranslator,
  CcfListItem,
  CcfDraggableIcon,
  CcfTypography,
  CcfBox,
  CcfSingleArrowIcon,
} from '@nice-ccf/ui-controls';
import { KeyboardEvent, useState } from 'react';
import { Stack, Box, useTheme, Theme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Navigation } from '../../../enums/navigation-menus';
import {
  getSelectedMenuName,
  getQuickAppNavigationItems,
  globalActions,
  getPanelAppNavigationItems,
} from '../../global.app.slice';
import CcfMenuIconRenderer from '../../ccf-app-navigation/ccf-menu-icon-renderer';
import { NavigationItem } from '../../ccf-navigation/useNavigationItems';
import { StorageKeys } from '@nice-ccf/core-sdk';
import { createStyles, makeStyles } from '@mui/styles';
import { selectAppSpaceActiveTab } from '@nice-ccf/ui-components';
import ccfAppHamburgerMenuForAppSpaceStyles from './ccf-app-hamburger-menu-for-app-space.styles';

export interface CcfAppHamburgerMenuForAppSpaceProps {
  isDrawerOpen?: boolean;
  toggleDrawer: (isDrawerOpen: boolean) => void;
  isAppSpaceMenu?: boolean;
  appSpaceNavigationList: any;
  helpUrl?: string;
}

/**
 * Used for Application Navigation
 * @param props - ? - CcfAppHamburgerMenuProps
 * @example `<CcfAppHamburgerMenuForAppSpace />`
 */
export const CcfAppHamburgerMenuForAppSpace = (
  props: CcfAppHamburgerMenuForAppSpaceProps
) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      draggableItem: {
        '& >div': {
          paddingTop: '10px',
          paddingBottom: '10px',
          opacity: '0.9',
          paddingRight: '6px',
          '&:hover, &:visited': {
            backgroundColor: '#343A41',
          },
        },
        '&:nth-child(8)': {
          '&>div': {
            paddingBottom: '15px',
          },
        },
        '&:nth-child(9)': {
          '&>div': {
            paddingTop: '15px',
          },
        },
        '& button.popOverOverflow ': {
          maxHeight: '24px',
          '& span': {
            marginLeft: '3px',
          },
        },
      },
      dragIcon: {
        cursor: 'grab',
      },
      menuActive: {
        backgroundColor: '#343A41',
      },
      hamburgerStack: {
        position: 'relative',
      },
    })
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const [translate] = useTranslator();
  const theme: Theme = useTheme();
  const selectedMenuAppSpace = useSelector(selectAppSpaceActiveTab);
  const { isAppSpaceMenu, appSpaceNavigationList } = props;
  const panelAppNavigationItems = useSelector(getPanelAppNavigationItems);
  const navigationItemsLS = panelAppNavigationItems
  const [dragStartMenuId, setDragStartMenuId] = useState('');
  const { setSelectedMenu, reArrangeMenu } = globalActions;

  const appHamburgerMenuStyles = ccfAppHamburgerMenuForAppSpaceStyles(theme);

  /**
   * Used to swtich menus
   * @param name - string
   * @param path - string
   * @example - setMenu
   */
  const setMenu = (name: Navigation) => {
    dispatch(setSelectedMenu({ name: name }));
  };

  /**
   * used to over-ride the styles on drag and drop event
   * @param style - applied drag and drop styles
   * @param snapshot - snapshot of current drag status
   * @example - getStyle
   */
  function getStyle(style: any, snapshot: any) {
    if (!snapshot.isDropAnimating) {
      return {
        ...style,
        backgroundColor: snapshot.isDragging ? `#343A41` : 'inherit',
        cursor: 'pointer',
      };
    }
    return {
      ...style,
      transitionDuration: '0.01s',
    };
  }

  /**
   * onDragStart used to set id of menu where dragging started
   * @param start - variable sent on onDrag event
   * @example - onDragStart
   */
  function onDragStart(start: any) {
    setDragStartMenuId(start.source.droppableId);
  }

  /**
   *
   * onKeyUpHendler used to swtich menus using keybord event
   * @param e - keyup event object
   * @param menuName - selected menu name
   * @example - onKeyUpHendler
   */
  function onKeyUpHandler(
    e: KeyboardEvent<HTMLDivElement>,
    menuName: Navigation
  ) {
    if (e.code === 'Enter') {
      setMenu(menuName);
    }
  }

  const globalApps: NavigationItem[] = [];

  navigationItemsLS.forEach((navItem: NavigationItem) => {
    globalApps.push(navItem);
  });

  return (
    <nav>
      <DragDropContext
        onDragEnd={(param) => {
          if (param.source && param.destination) {
            const navigationItemsLS = JSON.parse(
              localStorage.getItem(StorageKeys.CXONE_NAVIGATION_ITEMS) || '{}'
            );
            dispatch(
              reArrangeMenu({
                src: param.source,
                dest: param.destination,
                isAppSpaceMenu: isAppSpaceMenu,
                navigationItemsLS: navigationItemsLS,
                contactSpecificAppsCount: 0,
                navigationItemList: appSpaceNavigationList,
              })
            );
          }
        }}
        onDragStart={onDragStart}
      >
        <Stack className={classes.hamburgerStack}>
          <Droppable
            droppableId="droppable-3"
            isDropDisabled={
              dragStartMenuId !== 'droppable-2' &&
              dragStartMenuId !== 'droppable-3'
            }
          >
            {(provided, _) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {globalApps?.length > 0 &&
                  globalApps?.map(({ menuName, tooltip }, index) => {
                    return (
                      <Draggable
                        key={menuName}
                        draggableId={`draggable-${menuName}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            key={menuName}
                            id={`agent_appSpace_popOverMenu_CustomizeToolbar_${menuName}`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getStyle(
                              provided.draggableProps.style,
                              snapshot
                            )}
                            className={classes.draggableItem}
                            tabIndex={0}
                            role="button"
                            onKeyUp={(e) => onKeyUpHandler(e, menuName)}
                          >
                            <CcfListItem
                              button
                              selected={selectedMenuAppSpace === menuName}
                              classes={{ selected: classes.menuActive }}
                            >
                              <CcfMenuIconRenderer
                                menuName={menuName}
                                htmlColor={theme.palette.background.paper}
                              />
                              <Box
                                component="span"
                                sx={appHamburgerMenuStyles.menuName}
                              >
                                {menuName}
                              </Box>
                              <CcfDraggableIcon className={classes.dragIcon} />
                            </CcfListItem>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </Stack>
      </DragDropContext>
    </nav>
  );
};

export default CcfAppHamburgerMenuForAppSpace;
